import React from "react";

/**
 * The FusionBlade mark: a shield carrying a road that curves away toward the horizon.
 *
 * Hand-drawn from the approved artwork rather than an imported file, so it renders at any size and
 * rasterises cleanly for the printed cab card. If the brand file is ever added to the repo, replace
 * the shield group here rather than adding a second mark somewhere else — the cab card, the console
 * and anything else that brands itself should all resolve to this component.
 */

const BLADE_BLUE = "#4F5BF5";

export const FusionBladeMark: React.FC<{ size?: number; className?: string }> = ({ size = 64, className }) => (
    <svg
        width={size}
        height={size * 1.13}
        viewBox="0 0 160 181"
        className={className}
        role="img"
        aria-label="FusionBlade"
    >
        <defs>
            <clipPath id="fbShieldClip">
                <path d="M80 5 C60 16 40 20 14 20 L14 96 C14 134 44 160 80 176 C116 160 146 134 146 96 L146 20 C120 20 100 16 80 5 Z" />
            </clipPath>
            <linearGradient id="fbSky" x1="0" y1="0" x2="0.4" y2="1">
                <stop offset="0%" stopColor="#7B84FF" />
                <stop offset="100%" stopColor={BLADE_BLUE} />
            </linearGradient>
        </defs>

        {/* Shield field */}
        <g clipPath="url(#fbShieldClip)">
            <rect x="0" y="0" width="160" height="181" fill="url(#fbSky)" />
            {/* The dark ground the road runs over */}
            <path d="M0 92 C40 78 74 84 100 104 C124 122 140 150 148 181 L0 181 Z" fill="#12141C" />
            {/* Roadway, narrowing to the horizon */}
            <path d="M18 181 C40 132 70 104 116 88 L138 96 C96 116 68 146 54 181 Z" fill="#3A4150" />
            {/* Outer edge line and the dashed centre line */}
            <path d="M18 181 C40 132 70 104 116 88 L120 92 C78 110 50 140 32 181 Z" fill="#F2F5F8" opacity="0.9" />
            <g fill="#F2F5F8" opacity="0.95">
                <rect x="52" y="156" width="9" height="16" rx="2" transform="rotate(-38 52 156)" />
                <rect x="70" y="132" width="8" height="14" rx="2" transform="rotate(-44 70 132)" />
                <rect x="88" y="114" width="7" height="12" rx="2" transform="rotate(-52 88 114)" />
                <rect x="104" y="102" width="6" height="10" rx="2" transform="rotate(-58 104 102)" />
            </g>
            {/* Sweep of sky over the ridge */}
            <path d="M0 74 C44 58 92 62 128 84 L128 60 C92 44 44 44 0 58 Z" fill="#FFFFFF" opacity="0.16" />
        </g>

        {/* Shield outline, drawn last so it stays crisp over the artwork */}
        <path
            d="M80 5 C60 16 40 20 14 20 L14 96 C14 134 44 160 80 176 C116 160 146 134 146 96 L146 20 C120 20 100 16 80 5 Z"
            fill="none"
            stroke="#0B0D12"
            strokeWidth="9"
            strokeLinejoin="round"
        />
        <path
            d="M80 15 C62 25 44 29 22 29 L22 96 C22 130 48 153 80 167 C112 153 138 130 138 96 L138 29 C116 29 98 25 80 15 Z"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="4"
            strokeLinejoin="round"
            opacity="0.9"
        />
    </svg>
);

/**
 * The full lock-up — mark, "FusionBlade", and the Omnisight endorsement.
 *
 * `tone="dark"` is for printing on white (the cab card); `tone="light"` inverts the wordmark for the
 * console's dark chrome.
 */
export const FusionBladeLogo: React.FC<{ height?: number; tone?: "dark" | "light"; className?: string }> = ({
    height = 52,
    tone = "dark",
    className,
}) => {
    const ink = tone === "dark" ? "#0B0D12" : "#FFFFFF";
    return (
        <span className={`inline-flex items-center gap-3 ${className ?? ""}`} style={{ height }}>
            <FusionBladeMark size={height * 0.92} />
            <span className="flex flex-col justify-center leading-none">
                <span
                    style={{
                        fontSize: height * 0.46,
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                        color: ink,
                    }}
                >
                    Fusion<span style={{ color: BLADE_BLUE }}>Blade</span>
                </span>
                <span
                    style={{
                        marginTop: height * 0.1,
                        fontSize: height * 0.19,
                        letterSpacing: "0.18em",
                        color: ink,
                        opacity: 0.85,
                    }}
                >
                    BY OMNISIGHT
                </span>
            </span>
        </span>
    );
};
